import { LabCriteriasDTO } from "@/Common/Api/Labs/Dto/Responses/LabCriteriasDTO";
import { LabActionDto, LabMessageDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { SiteHelper } from "@/Common/Helpers/SiteHelper";
import { Guid } from "@/Common/Models/Guid";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { LabActionInboxes, LabActionInboxSortOrderType, LabInboxes, SortDirection } from "@/Common/Models/UI/Enums";
import { ConsultationRequestPriorityOption, ExamRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ManualLabsInboxFilteringConfiguration } from "@/Labs/Components/ManualLabsInboxSettingsContainer";
import { LabSettings } from "@/Labs/Models/Settings/LabSettings";
import { ISideNoteModel } from "@/Labs/Models/UI/ISideNoteModel";
import { ILabModel } from "../../Models/UI/ILabModel";

class LabsAction extends BaseAction {
    public SaveLabActionNote(
        labActionsDTOs: LabActionDto[],
        noteText: string,
        labFillerId: string,
        elementId: string,
        details: string,
        isManualLabEntry: boolean = false
    ): void {
        const requestData = {
            LabFillerId: labFillerId,
            ElementId: elementId,
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            NoteText: noteText ?? "",
            LabActionDTO: labActionsDTOs,
            Details: details,
            IsManualEntryNote: isManualLabEntry
        };

        return this.Execute(ServerOperations.Server.Myle_SaveLabActionsNotes, requestData);
    }

    public SaveLabSettings(userLoginId: string, labSettings: LabSettings): void {
        const requestData = {
            UserLoginId: userLoginId,
            Settings: labSettings
        };

        this.Execute(ServerOperations.Server.Myle_Lab_SaveLabSettings, requestData);
    }

    public GetNewLabsCount(): void {
        const requestData = {
            LicenseNumber: sessionStore.getCurrentEmployeeLicenseDto(),
            LicenseNumbers: labsStore.GetSharedOrphansLicences(),
            LabContexts: SiteHelper.GetContexts(),
            HideResultsFiledByColleague: labsStore.counterSettingsHideResultsFiledByColleague ?? false
        };
        return this.Execute(ServerOperations.Server.ARLMYLE_GetUnreadCount, requestData);
    }

    public GetLabsActionsCount(): void {
        return this.Execute(ServerOperations.Server.Myle_GetActionsCount, {});
    }

    public UpdateNewManualLabResultCount(): void {
        return this.Execute(ServerOperations.Server.Myle_ManualLabResults_GetNewManualLabResultsCount, {});
    }

    public RefreshActions(inboxtype: LabActionInboxes, searchtext: string, sortColumn: LabActionInboxSortOrderType, sortDirection: SortDirection, dateFrom: Date = labsStore.LabsActionInboxModel.get("DateFrom"), dateTo: Date = labsStore.LabsActionInboxModel.get("DateTo")): void {
        const requestData = {
            InboxType: inboxtype,
            SearchText: searchtext,
            SortDirection: sortDirection,
            SortColumn: sortColumn,
            DateTo: dateTo,
            DateFrom: dateFrom
        };

        // Throlle
        if (labsStore.timeoutReferenceRefreshLab !== -1) {
            clearTimeout(labsStore.timeoutReferenceRefreshLab);
            labsStore.timeoutReferenceRefreshLab = -1;
        }
        labsStore.timeoutReferenceRefreshLab = window.setTimeout(() => {
            this.Execute(ServerOperations.Server.Myle_Lab_GetLabActionsInbox, requestData);
        }, 250);
    }

    public RefreshLabs(forceSpecificCriterias: LabCriteriasDTO = null, subOperation: number = SubServerOperations.Labs.DefaultGetInboxCallbkack): void {
        if (labsStore.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults) {
            this.GetManualEntries();
        }
        else {
            const requestData: IARLMYLE_Labs_GetInBoxRequest = {
                labCriterias: forceSpecificCriterias ?? labsStore.GetLabInboxCriterias()
            };

            // Throttled
            clearTimeout(labsStore.timeoutReferenceRefreshLab);
            labsStore.timeoutReferenceRefreshLab = window.setTimeout(() => {
                this.Execute(ServerOperations.Server.ARLMYLE_Labs_GetInBox, requestData, subOperation);
            }, 250);
        }
    }

    public DeleteAction(labActionId: string): void {
        const requestData = {
            LabActionId: labActionId
        };

        this.Execute(ServerOperations.Server.Myle_Lab_DeleteLabAction, requestData);
    }

    public GetLabActionsAndNotes(labFillerId: string): void {
        const requestData = {
            labFillerIds: [labFillerId],
            requestId: labFillerId
        };

        this.Execute(ServerOperations.Server.Myle_Lab_GetActionsAndNotes, requestData);
    }

    public CompleteAction(labActionId: string, completedByContactFacetId: string): void {
        const requestData = {
            LabActionId: labActionId,
            CompletedBy: completedByContactFacetId,
            IsCompleted: true
        };

        this.Execute(ServerOperations.Server.Myle_Lab_ChangeLabActionStatus, requestData);
    }

    public LinkPatient(labFillerId: string, contactId: string): void {
        const requestData: ILinkPatient = {
            labFillerID: labFillerId,
            individualId: contactId
        };

        this.dispatch(ServerOperations.Server.Myle_ARLMYLE_LinkLabToPatient, requestData);
    }

    public HoldLabAction(labFillerIDs: string[], refreshInboxAfterAction: boolean): void {
        const currentEMP = sessionStore.getCurrentEmployee();
        const prefix = currentEMP.Model.PrefixLookup?.GetValue() ?? "";

        const requestData = {
            LabfillerIDs: labFillerIDs,
            ActionType: 1, // 1 on hold, 2 file
            RequestingUser_LicenseNumber: sessionStore.getCurrentEmployeeLicenseDto(),
            RequestingUser_Name: prefix + " " + currentEMP.Model.FirstName + ", " + currentEMP.Model.LastName,
            RefreshInboxAfterAction: refreshInboxAfterAction

        };
        this.Execute(ServerOperations.Server.ARLMYLE_TakeAction, requestData);
    }

    public FileLabAction(labFillerIDs: string[], refreshInboxAfterAction: boolean): void {
        const currentEMP = sessionStore.getCurrentEmployee();
        const prefix = currentEMP.Model.PrefixLookup?.GetValue() ?? "";

        const requestData = {
            LabfillerIDs: labFillerIDs,
            ActionType: 2, // 1 on hold, 2 file
            RequestingUser_LicenseNumber: sessionStore.getCurrentEmployeeLicenseDto(),
            RequestingUser_Name: prefix + " " + currentEMP.Model.FirstName + ", " + currentEMP.LastName,
            RefreshInboxAfterAction: refreshInboxAfterAction
        };

        this.Execute(ServerOperations.Server.ARLMYLE_TakeAction, requestData);
    }

    public GetEncapsulatedData(EncapsulatedDataId: string): void {
        const labAttachmentUrl = apiSettings.buildAppUrl(`api/labs/downloadFile/${EncapsulatedDataId}`, { auth: true });
        MyleHelper.OpenNewWindow(labAttachmentUrl);
    }

    public GetLab(
        labFillerID: string,
        suboperation: SubServerOperations.Labs = SubServerOperations.Labs.GetLab_Default,
        isManualLabEntry: boolean = false
    ): void {
        const requestData = {
            labFillerID: labFillerID,
            LicenseNumber: sessionStore.getCurrentEmployeeLicenseDto(),
            PatientContactId: labsStore.GetLabInboxCriterias()?.PatientContactId ?? null,
            RequestedResultIsAManualLabEntry: isManualLabEntry
        };

        // Throttled
        if (labsStore.timeoutReferenceGetLab !== -1) {
            clearTimeout(labsStore.timeoutReferenceGetLab);
            labsStore.timeoutReferenceGetLab = -1;
        }
        labsStore.timeoutReferenceGetLab = window.setTimeout(
            () => this.Execute(ServerOperations.Server.ARLMYLE_GetLab, requestData, suboperation),
            250
        );
    }

    public GetManualEntries(resetPageNumber: boolean = false): void {
        if (labsStore.timeoutReferenceRefreshLab !== -1) {
            clearTimeout(labsStore.timeoutReferenceRefreshLab);
            labsStore.timeoutReferenceRefreshLab = -1;
        }

        const labCriterias = labsStore.getManualLabsInboxFilteringConfiguration();
        labCriterias.pageNumber = resetPageNumber ? 1 : labCriterias.pageNumber;

        const requestData: { FilterSettings: ManualLabsInboxFilteringConfiguration } = {
            FilterSettings: labCriterias
        };

        labsStore.timeoutReferenceRefreshLab = window.setTimeout(
            () => this.Execute(ServerOperations.Server.Myle_ManualLabResults_GetManualLabResults, requestData),
            250
        );
    }

    public GetPatient(individualID: string): void {
        const requestData = {
            IndividualId: individualID,
            IncludeRelatedRecalls: true
        };
        this.Execute(ServerOperations.Server.Myle_LoadPatient, requestData);
    }

    public SaveEncounter(
        labModel: ILabModel,
        sideNoteModel: ISideNoteModel,
        isSignedOff: boolean,
        subOperation: number,
        additionalSignOffInformation?: AdditionalSignOffInformation
    ): void {
        let labRequestCustomDTO = null;
        if (sideNoteModel.get("Requete") != null && sideNoteModel.get("Requete") != "") {
            labRequestCustomDTO = {
                Request: sideNoteModel.get("Requete"),
                PriorityDropdownId: sideNoteModel.get("UrgentRequete")
                    ? ExamRequestPriorityOption.Urgent.model.Id
                    : ExamRequestPriorityOption.Normal.model.Id
            };
        }

        let labConsultationRequestDTO = null;
        const SpecialityString = sideNoteModel.get("ConsultationSpeciality");

        if (!IsNullOrWhiteSpace(SpecialityString)) {
            let recipient = sideNoteModel.get("ConsultationDestinataireFreeText");
            if (sideNoteModel.get("ConsultationDestinataire") != null && sideNoteModel.get("ConsultationDestinataire").count() > 0)
                recipient = sideNoteModel.get("ConsultationDestinataire").first().Name + " (" + sideNoteModel.get("ConsultationDestinataire").first().Licence + ")";

            labConsultationRequestDTO = {
                Speciality: sideNoteModel.get("ConsultationSpeciality"),
                Recipient: recipient,
                Description: sideNoteModel.get("Consultation"),
                PriorityDropdownId: sideNoteModel.get("UrgentConsultation")
                    ? ConsultationRequestPriorityOption.Urgent.model.Id
                    : ConsultationRequestPriorityOption.Normal.model.Id
            };
        }
        const requestData = {
            labEncounterDTO: {
                SiteId: sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
                RequestingStaffContactFacetId: sessionStore.getCurrentEmployee().ContactFacetId,
                IsSignedOff: isSignedOff,
                VisitPurpose: sideNoteModel.get("Raison") ? sideNoteModel.get("Raison") : "",
                RadiologyNote: sideNoteModel.get("Imagerie") ? sideNoteModel.get("Imagerie") : "",
                LabNotes: sideNoteModel.get("Laboratoire") ? sideNoteModel.get("Laboratoire") : "",
                OtherPlanNote: sideNoteModel.get("FollowUp") ? sideNoteModel.get("FollowUp") : "",
                HistoryNote: "",
                ExamNote: "",
                OtherProgressNote: "",
                PatientId: labModel.get("MylePatient").PatientResponse.ContactFacet_id,
                labConsultationRequestDTO: labConsultationRequestDTO,
                labRequestCustomDTO: labRequestCustomDTO,
                ConsultationTypeLookupId: labsStore.Model.get("CurrentSpeciality").LookupID,
                ConsultationContextLookupId: cacheStore.FindLookupByLookupCode("Laboratory", "CONSULTATIONCONTEXT").LookupID,
                AppointmentId: additionalSignOffInformation?.appointmentId,
                InterfaceNoteCategory: additionalSignOffInformation?.interfaceNoteCategory
            },
            OpenEncounterAfterSave: !isSignedOff
        };

        return this.Execute(
            ServerOperations.Server.Myle_CreateLabEncounter,
            requestData,
            subOperation,
            null,
            () => additionalSignOffInformation?.ValidationCallback(false),
            () => additionalSignOffInformation?.ValidationCallback(true)
        );
    }

    public LoadGraphData(patientContactId: string, checkedIdentifiers: string[], healthCardNumber?: string): void {
        const requestData: ILoadGraphData = {
            patientContactId,
            checkedIdentifiers,
            healthCardNumber
        };

        return this.dispatch(ServerOperations.Server.ARLMYLE_GetObservations, requestData);
    }

    public LoadPortalSharedItemForLab(sharedItemId: string): void {
        const requestData =
        {
            SharedItemId: sharedItemId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, requestData, SubServerOperations.PortalLinkedItemStatus.Results);
    }

    public SendLabToPortal(
        objectId: string,
        sharedItemId: string,
        subject: string,
        sharedItemType: PortalLinkedItemType,
        employeeContactId: string,
        patientContactId: string,
        createdByUserId: string,
        portalMessages: SharedItemMessage[],
        lab: LabMessageDTO,
        includeLinkedItem: boolean,
        linkedItemUpdated: boolean,
        canPatientReplyWithMessage: boolean
    ): void {
        const requestData =
        {
            ObjectId: objectId,
            SharedItemType: sharedItemType,
            Subject: subject,
            EmployeeContactId: employeeContactId,
            PatientContactId: patientContactId,
            CreatedByUserId: createdByUserId,
            IncludeLinkedItem: includeLinkedItem,
            Messages: portalMessages,
            SharedItemId: sharedItemId,
            LabMessageDTO: lab,
            LinkedItemUpdated: includeLinkedItem && linkedItemUpdated,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, requestData);
    }

    public RevokeLabSharedItemToPortal(sharedItemId: string, sharedItemMessages: number[], userId: string, sharedItemType: PortalLinkedItemType, labFillerId: string, includeLinkedItem: boolean): void {
        const requestData =
        {
            SharedItemId: sharedItemId,
            SharedItemMessageIds: sharedItemMessages,
            UserId: userId,
            IncludeLinkedItem: includeLinkedItem,
            SharedItemType: sharedItemType,
            ObjectId: labFillerId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, requestData);
    }

    public loadAttachedTasks(labFilledId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_LoadAttachedCommunications,
            { LabFillerId: labFilledId },
            SubServerOperations.AttachedCommunications.Labs
        );
    }

    public loadRamqInfo(patientId: string): void {
        this.Execute(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patientId
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );
    }

    public createPatient(patientModel: IPatientCreationModel): void {
        return this.Execute(
            ServerOperations.Server.Myle_CreatePatient,
            PatientHelper.PrepareCreateNewPatientRequest(patientModel)
        );
    }

    public async FileManualLabResults(labFillerIds: Guid[], refreshLabs: boolean): Promise<void> {
        await this.ExecuteAsync(ServerOperations.Server.Myle_ManualLabResults_FileManualLabResults, {
            LabFillerIds: labFillerIds
        });

        if (refreshLabs) {
            this.RefreshLabs();
        }
        else {
            labsStore.LabActions.UpdateNewManualLabResultCount();
        }
    }
}

interface ILinkPatient {
    labFillerID: string;
    individualId: string;
}

interface ILoadGraphData {
    patientContactId: string,
    checkedIdentifiers: string[],
    healthCardNumber: string
}

interface IARLMYLE_Labs_GetInBoxRequest {
    labCriterias: LabCriteriasDTO
}

export { IARLMYLE_Labs_GetInBoxRequest, ILinkPatient, ILoadGraphData, LabsAction };
